import { useEffect, useState, useCallback } from "react";
import { Questionary } from "../../../models/Questionary";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Stack,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import ArchiveButton from "../../../Helpers/archiveButton";
import {
  faBackwardStep,
  faBan,
  faBoxOpen,
  faForwardStep,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import QuestionaryService, {
  PagedResult,
} from "../../../services/questionary.service";
import useUser from "../../App/useUser";

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Type guard para detectar si es PagedResult o Array
function isPagedResult<T>(
  response: T[] | PagedResult<T>,
): response is PagedResult<T> {
  return (response as PagedResult<T>).data !== undefined;
}

// Debounce hook para búsqueda
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================
// COMPONENT
// ============================================

type Props = {
  onHide: () => void;
  show: boolean;
  onRestore: (questionary: Questionary) => void;
};

const ModalArchivedQuestionaries = (props: Props) => {
  // Estados principales
  const [archivedQuestionaries, setArchivedQuestionaries] = useState<
    Questionary[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  // Estados de búsqueda y paginación
  const [filterText, setFilterText] = useState("");
  const debouncedSearchText = useDebounce(filterText, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // 10 items por página

  // Estados de paginación del servidor
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Cargar cuestionarios archivados cuando se abre el modal o cambian los filtros
  const loadArchivedQuestionaries = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      let result: Questionary[] | PagedResult<Questionary>;

      // Llamar al endpoint según el rol del usuario CON PAGINACIÓN
      if (user.role === "Administrador") {
        result = await QuestionaryService.getArchivedQuestionaries(
          undefined,
          undefined,
          "Administrador",
          currentPage,
          itemsPerPage,
          debouncedSearchText,
        );
      } else if (user.role === "Auditor") {
        result = await QuestionaryService.getArchivedQuestionaries(
          user.id,
          undefined,
          "Auditor",
          currentPage,
          itemsPerPage,
          debouncedSearchText,
        );
      } else {
        result = await QuestionaryService.getArchivedQuestionaries(
          undefined,
          user.email || "",
          "User",
          currentPage,
          itemsPerPage,
          debouncedSearchText,
        );
      }

      // Detectar tipo de respuesta usando type guard
      if (isPagedResult(result)) {
        // Es PagedResult - modo optimizado
        setArchivedQuestionaries(result.data);
        setTotalRecords(result.totalRecords);
        setTotalPages(result.totalPages);
      } else {
        // Es Array - modo legacy (fallback)
        setArchivedQuestionaries(result);
        setTotalRecords(result.length);
        setTotalPages(Math.ceil(result.length / itemsPerPage));
      }
    } catch (error) {
      console.error("Error loading archived questionaries:", error);
      setError("No se pudieron cargar los cuestionarios archivados.");
    } finally {
      setLoading(false);
    }
  }, [user, currentPage, itemsPerPage, debouncedSearchText]);

  // Cargar datos cuando se abre el modal o cambian las dependencias
  useEffect(() => {
    if (props.show && user) {
      loadArchivedQuestionaries();
    }
  }, [props.show, loadArchivedQuestionaries]);

  // Resetear a página 1 cuando cambia el filtro o items per page
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, itemsPerPage]);

  const FormattedDate = ({
    isoDateString,
  }: {
    isoDateString: string | undefined;
  }) => {
    if (isoDateString === undefined) return <span>N/A</span>;
    const date = new Date(isoDateString);
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    } as const;
    return (
      <span>{new Intl.DateTimeFormat("es-ES", options).format(date)}</span>
    );
  };

  const handleRestore = async (questionary: Questionary) => {
    await props.onRestore(questionary);
    // Recargar la lista después de restaurar
    await loadArchivedQuestionaries();
  };

  const handleClose = () => {
    setFilterText("");
    setCurrentPage(1);
    setError(null);
    props.onHide();
  };

  // Helper para obtener nombres compatibles con DTOs y entidades
  const getUserName = (q: any) => {
    return q.userName || q.user?.name || "";
  };

  const getQuestionaryTypeName = (q: any) => {
    return q.questionaryTypeName || q.questionaryType?.name || "";
  };

  // Generar items de paginación
  const renderPaginationItems = () => {
    if (totalPages === 0) return null;

    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <Button
          key={1}
          variant={currentPage === 1 ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setCurrentPage(1)}
        >
          1
        </Button>,
      );
      if (startPage > 2) {
        items.push(
          <span key="ellipsis-start" className="mx-1">
            ...
          </span>,
        );
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Button
          key={page}
          variant={currentPage === page ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setCurrentPage(page)}
          className="mx-1"
        >
          {page}
        </Button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="ellipsis-end" className="mx-1">
            ...
          </span>,
        );
      }
      items.push(
        <Button
          key={totalPages}
          variant={currentPage === totalPages ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Button>,
      );
    }

    return items;
  };

  // Calcular rango de registros mostrados
  const startRecord =
    totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <Modal
      show={props.show}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      onHide={handleClose}
      size="xl"
    >
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          Cuestionarios archivados
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Alert de error */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Búsqueda y selector de items */}
        <Row className="mb-3 g-3 align-items-center">
          <Col md={5}>
            <Form.Control
              type="text"
              placeholder="Buscar..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              disabled={loading}
            />
          </Col>
          <Col md={7}>
            <Row className="align-items-center">
              <Col className="text-end">
                <small className="text-muted me-3">
                  {totalRecords} cuestionario(s) archivado(s)
                </small>
                <Form.Group
                  as={Row}
                  className="align-items-center justify-content-end d-inline-flex"
                >
                  <Form.Label
                    column
                    sm="auto"
                    className="small text-muted me-2"
                  >
                    Mostrar:
                  </Form.Label>
                  <Col sm="auto">
                    <Form.Select
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      style={{ width: "auto" }}
                      disabled={loading}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Form.Select>
                  </Col>
                  <Form.Label
                    column
                    sm="auto"
                    className="small text-muted ms-2"
                  >
                    por página
                  </Form.Label>
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Loading */}
        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
          </div>
        )}

        {/* Info de registros */}
        {!loading && totalRecords > 0 && (
          <div className="text-muted mb-2 small">
            Mostrando {startRecord} a {endRecord} de {totalRecords} registros
          </div>
        )}

        {/* Sin resultados */}
        {!loading && archivedQuestionaries.length === 0 && (
          <p className="text-center" style={{ marginTop: 40 }}>
            {filterText
              ? "No se encontraron resultados para la búsqueda."
              : "No hay cuestionarios archivados."}
          </p>
        )}

        {/* Tabla de resultados */}
        {!loading && archivedQuestionaries.length > 0 && (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr className="text-center">
                  <th>Id</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Tipo</th>
                  <th>Fecha creación</th>
                  <th>Usuario</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {archivedQuestionaries.map((q) => (
                  <tr key={q.id}>
                    <td className="text-center">{q.id}</td>
                    <td>{q.name}</td>
                    <td>{q.email}</td>
                    <td>{getQuestionaryTypeName(q)}</td>
                    <td className="text-center">
                      <FormattedDate
                        isoDateString={q.creationDate?.toString()}
                      />
                    </td>
                    <td className="text-center">{getUserName(q)}</td>
                    <td className="text-center">
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="justify-content-center"
                      >
                        <ArchiveButton
                          title="Restaurar"
                          description="¿Estás seguro de que deseas restaurar este registro?"
                          iconButton={faBoxOpen}
                          variant="primary"
                          onClick={() => handleRestore(q)}
                        />
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3 align-items-center gap-2">
                <Button
                  variant={currentPage === 1 ? "secondary" : "primary"}
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                >
                  <FontAwesomeIcon icon={faBackwardStep} />
                </Button>

                {renderPaginationItems()}

                <Button
                  variant={currentPage === totalPages ? "secondary" : "primary"}
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                >
                  <FontAwesomeIcon icon={faForwardStep} />
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          <FontAwesomeIcon icon={faBan} style={{ paddingRight: "3px" }} />
          <span className="ms-2">Cerrar</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalArchivedQuestionaries;
